import React, { useEffect, useState } from 'react';
import { campaignAPI, Campaign } from '../services/api';
import Icon from './Icon';
import { useT } from '../i18n/LanguageContext';

interface ScheduleBoxProps {
  campaign: Campaign;
  onChanged?: () => void;
}

/**
 * Schedule a campaign for future (one-time or recurring) sending, view the
 * current schedule, and cancel it. The backend scheduler executes due
 * campaigns every minute.
 */
const ScheduleBox: React.FC<ScheduleBoxProps> = ({ campaign, onChanged }) => {
  const t = useT();
  const [schedule, setSchedule] = useState<any | null>(null);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduleType, setScheduleType] = useState('once');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = async () => {
    try {
      const res = await campaignAPI.getSchedule(campaign.id);
      if (res.data.success) setSchedule(res.data.data.schedule);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id]);

  const handleSchedule = async () => {
    if (!scheduledAt) return;
    setBusy(true);
    setMsg(null);
    try {
      // datetime-local has no timezone; convert to ISO from local time
      const iso = new Date(scheduledAt).toISOString();
      const res = await campaignAPI.scheduleCampaign(campaign.id, { scheduledAt: iso, scheduleType });
      setMsg({ ok: true, text: res.data.message });
      await load();
      onChanged?.();
    } catch (err: any) {
      setMsg({ ok: false, text: err.response?.data?.error || t('Zakazivanje nije uspelo', 'Failed to schedule') });
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t('Otkazati zakazano slanje?', 'Cancel the scheduled send?'))) return;
    setBusy(true);
    setMsg(null);
    try {
      await campaignAPI.cancelSchedule(campaign.id);
      setMsg({ ok: true, text: t('Zakazivanje otkazano.', 'Schedule cancelled.') });
      setSchedule(null);
      await load();
      onChanged?.();
    } catch (err: any) {
      setMsg({ ok: false, text: err.response?.data?.error || t('Otkazivanje nije uspelo', 'Failed to cancel') });
    } finally {
      setBusy(false);
    }
  };

  const canSchedule = campaign.status === 'draft' || campaign.status === 'scheduled';

  return (
    <div className="card card-pad" style={{ marginTop: 24 }}>
      <h3 className="card-title" style={{ marginTop: 0, marginBottom: 12 }}>
        <Icon name="clock" size={16} /> {t('Zakazivanje', 'Schedule')}
      </h3>

      {schedule ? (
        <div
          style={{
            padding: 12,
            backgroundColor: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          <strong>{t('Zakazano:', 'Scheduled:')}</strong> {new Date(schedule.scheduledAt).toLocaleString()}
          {schedule.scheduleType && schedule.scheduleType !== 'once' && ` (${schedule.scheduleType})`}
          <button
            className="btn btn-danger btn-sm"
            onClick={handleCancel}
            disabled={busy}
            style={{ marginLeft: 12 }}
          >
            {t('Otkaži', 'Cancel')}
          </button>
        </div>
      ) : !canSchedule ? (
        <p className="hint" style={{ marginTop: 0 }}>
          {t('Zakazivanje je dostupno za kampanje u pripremi sa primaocima.', 'Scheduling is available for draft campaigns with recipients.')}
        </p>
      ) : (
        <>
          <p className="hint" style={{ marginTop: 0, marginBottom: 12 }}>
            {t('Izaberite budući datum/vreme. Kampanja se automatski šalje u tom trenutku.', 'Pick a future date/time. The campaign is sent automatically at that moment.')}
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="input"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              style={{ width: 'auto' }}
            />
            <select
              className="select"
              value={scheduleType}
              onChange={(e) => setScheduleType(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="once">{t('Jednom', 'Once')}</option>
              <option value="daily">{t('Dnevno', 'Daily')}</option>
              <option value="weekly">{t('Nedeljno', 'Weekly')}</option>
              <option value="monthly">{t('Mesečno', 'Monthly')}</option>
            </select>
            <button
              className="btn btn-primary"
              onClick={handleSchedule}
              disabled={busy || !scheduledAt}
            >
              {busy ? t('Zakazivanje…', 'Scheduling…') : t('Zakaži', 'Schedule')}
            </button>
          </div>
        </>
      )}

      {msg && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 5,
            fontSize: 14,
            backgroundColor: msg.ok ? 'var(--surface-2)' : 'var(--danger-soft)',
            color: msg.ok ? 'var(--brand-700)' : 'var(--danger)',
            border: `1px solid ${msg.ok ? 'var(--ok)' : 'var(--danger)'}`,
          }}
        >
          {msg.ok ? <Icon name="checkCircle" size={14} /> : <Icon name="x" size={14} />} {msg.text}
        </div>
      )}
    </div>
  );
};

export default ScheduleBox;
